import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateCarportCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const propertyIdentificationForTranasaction = propertyIdentification[id];

  if (
    isUndefined(improvementsForTransaction) ||
    isUndefined(propertyIdentificationForTranasaction)
  ) {
    return undefined;
  }

  const {
    carport,
    craftsmanQuality: dwellingQuality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;

  if (
    isUndefined(carport) ||
    isUndefined(carport.area) ||
    !ORDER_FEATURES.carport.hasFeature(improvementsForTransaction)
  ) {
    return undefined;
  }

  const {
    address: { PostalCode: zipCode },
  } = propertyIdentificationForTranasaction;

  if (
    isUndefined(dwellingQuality) ||
    (dwellingQuality * 10) % 1 !== 0 ||
    dwellingQuality < 1 ||
    isUndefined(zipCode)
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getCarportCost({
    sqft: AloftArea.schema.parse(carport.area),
    quality: carport.quality ?? dwellingQuality,
    propertyType,
    zipCode,
  });
};

const getTotalOrBreakdownCarportCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateCarportCost(costProps, improvements, propertyIdentification, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalCarportCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownCarportCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectCarportCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (_state, id: string) => id,
  ],
  getTotalCarportCost,
);

export const selectZipOnlyCarportCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (_state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownCarportCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectCarportCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalCarportCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectCarportCostDisplay = createSelector([selectCarportCost], (carportCost) => {
  if (isUndefined(carportCost)) {
    return undefined;
  }

  return carportCost.toString(true);
});
